/**
 * Camel Up service layer — Firestore adapter for the pure Camel Up engine.
 *
 * Public/private split (same shape as Scotland Yard's `mrx` private doc):
 *   - `rooms/{code}/state/main` holds the full `CamelUpState` EXCEPT that
 *     each race-bet pile slot's `color` is `null` while the game is in
 *     progress. Slot `uid` is public (so the TV can render pile heights and
 *     who placed them); the color is the secret bit.
 *   - `rooms/{code}/private/{uid}` holds a `CamelUpPrivateDoc` for each
 *     player — an append-only list of `{pile, color}` recording every
 *     race-bet that player has placed, in placement order. The owning
 *     player's client merges this back into the public state when
 *     subscribing.
 *   - At game end (`status === 'finished'`), the service writes the public
 *     state with ALL race-bet colors filled in, so the end-game review UI
 *     can render every player's bets without any private reads.
 *
 * Move dispatch (`applyMove`): a Firestore transaction reads the public
 * state plus every player's private subdoc, merges full race-bet colors
 * into the engine's view of state, runs `engineApplyMove`, then writes:
 *   - public state (sanitized unless game ended),
 *   - the actor's private doc (appended on `place-race-bet`),
 *   - the room doc (status / finishedAtMs / winner on game end).
 */

import {
  doc,
  onSnapshot,
  runTransaction,
  type DocumentData,
  type Firestore,
  type Unsubscribe,
} from 'firebase/firestore'
import { getDb } from '@/platform/firebase/client'
import {
  CamelUp,
  dbdRoom,
  dbdRoomPrivate,
  dbdRoomState,
} from '@mdev/common'
import {
  GameNotActiveError,
  IllegalMoveError,
  RoomNotInGameError,
} from '../lobby/errors'

type CamelUpState = CamelUp.CamelUpState
type Move = CamelUp.Move
type RacingColor = CamelUp.RacingColor
type RaceBetSlot = CamelUp.RaceBetSlot

const db = getDb()

/** Body of `rooms/{code}/private/{uid}`. */
export interface CamelUpPrivateDoc {
  /** Race-bet colors this player has placed, in placement order. */
  raceBets: Array<{ pile: 'winner' | 'loser'; color: RacingColor }>
}

// ---------------------------------------------------------------------------
// Public/private merge helpers
// ---------------------------------------------------------------------------

/**
 * Merge one player's private race-bet colors into a "public" `CamelUpState`
 * (where racePile slot colors are nulled). Slots whose `uid !== viewerUid`
 * stay nulled; slots whose `uid === viewerUid` get their color filled in
 * from `priv.raceBets` in placement order.
 *
 * Returns a fresh state — never mutates inputs.
 */
function mergePrivateForViewer(
  pub: CamelUpState,
  viewerUid: string,
  priv: CamelUpPrivateDoc | null,
): CamelUpState {
  if (priv === null) return pub
  const winnerSeq = priv.raceBets.filter((b) => b.pile === 'winner')
  const loserSeq = priv.raceBets.filter((b) => b.pile === 'loser')
  let wi = 0
  let li = 0
  const fillSlot = (
    slot: RaceBetSlot,
    pile: 'winner' | 'loser',
  ): RaceBetSlot => {
    if (slot.uid !== viewerUid) return slot
    const seq = pile === 'winner' ? winnerSeq : loserSeq
    const idx = pile === 'winner' ? wi++ : li++
    const entry = seq[idx]
    return { uid: slot.uid, color: entry ? entry.color : slot.color }
  }
  return {
    ...pub,
    racePiles: {
      winner: pub.racePiles.winner.map((s) => fillSlot(s, 'winner')),
      loser: pub.racePiles.loser.map((s) => fillSlot(s, 'loser')),
    },
  }
}

/** Strip all race-bet slot colors from a state, for public storage. */
function sanitizePublicState(state: CamelUpState): CamelUpState {
  return {
    ...state,
    racePiles: {
      winner: state.racePiles.winner.map((s) => ({ uid: s.uid, color: null })),
      loser: state.racePiles.loser.map((s) => ({ uid: s.uid, color: null })),
    },
  }
}

// ---------------------------------------------------------------------------
// Subscriptions
// ---------------------------------------------------------------------------

/**
 * Per-player live subscription. Streams the public state + the requesting
 * player's private race-bet subdoc, merging the player's own bet colors
 * into the state before invoking the callback.
 *
 * Other players' race-bet colors remain `null` in the merged state (until
 * game end, when the service writes them all to public). Host clients
 * (sentinel uid) get the pure public state — they never see any colors
 * until game end.
 */
export function subscribePlayerState(
  _firestore: Firestore,
  code: string,
  uid: string,
  callback: (state: CamelUpState | null) => void,
): Unsubscribe {
  let lastPublic: CamelUpState | null = null
  let lastPrivate: CamelUpPrivateDoc | null = null

  function emit() {
    if (lastPublic === null) {
      callback(null)
      return
    }
    callback(mergePrivateForViewer(lastPublic, uid, lastPrivate))
  }

  const pubRef = doc(db, ...dbdRoomState(code))
  const unsubPub = onSnapshot(pubRef, (snap) => {
    lastPublic = snap.exists() ? (snap.data() as CamelUpState) : null
    emit()
  })

  const privRef = doc(db, ...dbdRoomPrivate(code, uid))
  const unsubPriv = onSnapshot(privRef, (snap) => {
    lastPrivate = snap.exists() ? (snap.data() as CamelUpPrivateDoc) : null
    emit()
  })

  return () => {
    unsubPub()
    unsubPriv()
  }
}

// ---------------------------------------------------------------------------
// Move application
// ---------------------------------------------------------------------------

export interface ApplyMoveOpts {
  code: string
  move: Move
}

/**
 * Apply a Camel Up move to the live game in Firestore. Atomic across the
 * public state doc, every player's private subdoc, and (on game-end) the
 * room doc.
 */
export async function applyMove(opts: ApplyMoveOpts): Promise<void> {
  const { code, move } = opts

  const stateRef = doc(db, ...dbdRoomState(code))
  const roomRef = doc(db, ...dbdRoom(code))

  await runTransaction(db, async (tx) => {
    const stateSnap = await tx.get(stateRef)
    if (!stateSnap.exists()) throw new RoomNotInGameError(code)
    const publicState = stateSnap.data() as CamelUpState
    if (publicState.status === 'finished') {
      throw new GameNotActiveError(code, publicState.status)
    }

    // Read every player's private doc so we can merge complete race-bet
    // colors into the engine's view of state. (Needed both for engine
    // correctness on race-end resolution and so we have a coherent
    // pre-image to write back.) Build private refs from seatOrder; the
    // doc may not yet exist for players who've never placed a race bet.
    const privRefs = publicState.seatOrder.map((u) => ({
      uid: u,
      ref: doc(db, ...dbdRoomPrivate(code, u)),
    }))
    const privSnaps = await Promise.all(privRefs.map((p) => tx.get(p.ref)))
    const privates: Record<string, CamelUpPrivateDoc> = {}
    privRefs.forEach((p, i) => {
      privates[p.uid] = privSnaps[i].exists()
        ? (privSnaps[i].data() as CamelUpPrivateDoc)
        : { raceBets: [] }
    })

    // Merge every player's private colors into the public state to form the
    // engine's "full info" state.
    const fullState = mergeAllPrivatesIntoState(publicState, privates)

    // Run the engine.
    let result: { state: CamelUpState }
    try {
      result = CamelUp.applyMove({ state: fullState, move })
    } catch (err) {
      if (err instanceof CamelUp.EngineError) {
        throw new IllegalMoveError(err.code, err.message)
      }
      throw err
    }
    const nextFullState = result.state

    // If the move is a place-race-bet, append to that player's private doc.
    if (move.kind === 'place-race-bet') {
      const priv = privates[move.uid] ?? { raceBets: [] }
      const nextPriv: CamelUpPrivateDoc = {
        raceBets: [
          ...priv.raceBets,
          { pile: move.pile, color: move.color },
        ],
      }
      const privRef = doc(db, ...dbdRoomPrivate(code, move.uid))
      tx.set(privRef, nextPriv satisfies CamelUpPrivateDoc as DocumentData)
    }

    // Write the public state. While in progress: redact race-pile colors.
    // On game end: write full state so the end-game UI doesn't need to
    // re-merge privates client-side.
    const publicToWrite =
      nextFullState.status === 'finished'
        ? nextFullState
        : sanitizePublicState(nextFullState)
    tx.set(stateRef, publicToWrite as unknown as DocumentData)

    // Flip the room doc on game end (matches SY pattern).
    if (nextFullState.status === 'finished' && nextFullState.winner) {
      tx.update(roomRef, {
        status: 'finished',
        finishedAtMs: Date.now(),
        winner: {
          team: nextFullState.winner.uid,
          reason: `${nextFullState.winner.coins} coins`,
        },
      })
    }
  })
}

/** Merge every player's private race-bet colors into a full-info state. */
function mergeAllPrivatesIntoState(
  pub: CamelUpState,
  privates: Record<string, CamelUpPrivateDoc>,
): CamelUpState {
  // Per-uid cursors into the winner / loser sub-sequences.
  const winnerCursors: Record<string, number> = {}
  const loserCursors: Record<string, number> = {}
  for (const uid of pub.seatOrder) {
    winnerCursors[uid] = 0
    loserCursors[uid] = 0
  }
  const fill = (slot: RaceBetSlot, pile: 'winner' | 'loser'): RaceBetSlot => {
    if (slot.color !== null) return slot
    const priv = privates[slot.uid]
    if (!priv) return slot
    const seq = priv.raceBets.filter((b) => b.pile === pile)
    const cursor = pile === 'winner' ? winnerCursors[slot.uid]++ : loserCursors[slot.uid]++
    const entry = seq[cursor]
    return entry ? { uid: slot.uid, color: entry.color } : slot
  }
  return {
    ...pub,
    racePiles: {
      winner: pub.racePiles.winner.map((s) => fill(s, 'winner')),
      loser: pub.racePiles.loser.map((s) => fill(s, 'loser')),
    },
  }
}
